import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider
} from '@mui/material';

import { fetchAlertById as fetchAlertByIdService } from '../../../../services/modules/ecs/alert';
import { fetchDimensions as fetchDimensionsService } from 'services/modules/ecs/data';

import WarningIcon from '@mui/icons-material/Warning';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

function SingleAlertPage() {
  // useParams hook
  const { id } = useParams();

  // Redux dispatch
  const dispatch = useDispatch();
  
  // State variables
  const [showRemediationButton, setShowRemediationButton] = useState(true);
  const [mainDimensionLabel, setMainDimensionLabel] = useState('');
  const [threatActor, setThreatActor] = useState(false);
  
  // Redux selectors
  const dimensionsContainer = useSelector((state) => state.ecsData.dimensions);
  const threatContainer = useSelector((state) => state.ecsAlert.threat);
 
  // useEffect hook fetches alert by id and dimensions
  useEffect(() => {
    if (id !== null) {
      dispatch(fetchAlertByIdService({ id }));
    }
    dispatch(fetchDimensionsService({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect hook sets the main dimension label
  useEffect(() => {
    if (threatContainer && threatContainer.dimension && dimensionsContainer) {
      const mainDimension = dimensionsContainer
        .filter((dim) => !!dim.subDimensions)
        .find((dim) => dim.subDimensions.find((sub) => sub.id === threatContainer.dimension.id));

      if (mainDimension) {
        setMainDimensionLabel(mainDimension.label);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsContainer, threatContainer]);
  
  // Formats the property names 
  function formatPropertyName(property) {
    if (property === 'cve') {
      return property.toUpperCase();
    } else {
      return property.replace(/_/g, ' ').replace(/(?:^|\s)\S/g, function (a) {
        return a.toUpperCase();
      });
    }
  }
  
  // This method opens threat actor dialog in case if any threat actors are present
  const handleThreatActorClick = () => {
    if (threatContainer.data.ThreatActor !== 'Not Applicable') {
      setThreatActor(true);
    }
  };

  return (
    <>
      <div className='m-2'>
        {!!!mainDimensionLabel ? (
          <div className="loading-spinner-container ">
            <CircularProgress />
          </div>
        ) : (
          <Card>
            <CardHeader
              title={
                <React.Fragment>
                  <div className="card-head">
                    <div className="flex-head">
                      <label className="label">
                        {threatContainer && threatContainer.dimension.label}&nbsp;|&nbsp;{threatContainer && mainDimensionLabel}
                      </label>
                    </div>
                    <div>
                      <Button variant="contained" className="back font" onClick={() => window.history.back()}>
                        Back
                      </Button>
                    </div>
                  </div>
                </React.Fragment>
              }
            />
            <Divider className="divider" />
            <CardContent>
              <div className="row">
                <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                  <div>
                    <h3 className="text-bold">Impact</h3>
                    <div className="text-body1 text-grey-8">
                      {threatContainer.impact.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {line.trim()}
                          <br />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="remediation mx-6">
                    {showRemediationButton ? (
                      <Button
                        variant="outlined"
                        className="remediation-button mx-6"
                        onClick={() => setShowRemediationButton(false)}
                      >
                        <WarningIcon color="inherit" />
                        Show Remediations
                      </Button>
                    ) : (
                      <>
                        <h3 className="text-bold">Remediation</h3>
                        <div className="text-body1 text-grey-8">
                          {threatContainer.remediation.split('\n').map((line, index) => (
                            <React.Fragment key={index}>
                              {line.trim()}
                              <br />
                            </React.Fragment>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                  <Card>
                    <CardContent>
                      <h3 className="text-primary">Finding details</h3>
                      <div className="row">
                        <div className="col-4 text-body1" sx={{ m: 2 }}>
                          <b className="word-wrap">Evidence</b>:
                        </div>
                        <div className="col-8 text-body1 text-grey-8 word-wrap">
                          {threatContainer.evidence.split('\n').map((line, index) => (
                            <React.Fragment key={index}>
                              {line.trim()}
                              <br />
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                      {threatContainer &&
                        threatContainer.data &&
                        Object.keys(threatContainer.data)
                          .filter((property) => property !== 'ThreatActorDescription')
                          .map((property, index) => (
                            <React.Fragment key={index}>
                              <div className="row">
                                <div className="col-4 text-body1">
                                  <strong className="word-wrap">{property === 'ThreatActor' ? 'Threat Actor' : formatPropertyName(property)}</strong>:
                                </div>
                                <div className={`col-8 text-body1 ${property === 'domain' ? 'text-secondary' : property === 'cve' || property === 'ThreatActor' ? 'text-primary' : 'text-grey-8'}`} onClick={() => property === 'ThreatActor' && handleThreatActorClick()}>
                                  {typeof threatContainer.data[property] === 'string' ? (
                                    threatContainer.data[property].split('\n').map((line, index,array) => (
                                      <React.Fragment key={index}>
                                        {line.trim()}
                                        {index < array?.length - 1 && <br />}
                                      </React.Fragment>
                                    ))
                                  ) : (
                                    threatContainer.data[property]
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          ))}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Dialog open={threatActor} onClose={() => setThreatActor(false)}>
        <DialogTitle>
          Threat Actor Description
          <IconButton onClick={() => setThreatActor(false)} className = 'cross-icon'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {threatContainer && threatContainer.data &&
              <p className="preline">{threatContainer.data.ThreatActorDescription}</p>
            }
            <br></br>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SingleAlertPage;
